import React, { useState } from 'react';
import { LockIcon } from 'lucide-react';

// Password gate for internal index pages (the /find-degrees and /explore
// template directories). The individual template pages behind them stay
// public — this only keeps the list of every variant from being browsable.
//
// This is a convenience gate, not real security: the passphrase ships in the
// client bundle, so treat it as "not linked publicly" rather than protected.
const ACCESS_PHRASE = 'fod-admin-2026';
const STORAGE_KEY = 'fod:admin-directory';
interface AdminPasswordGateProps {
  // Shown above the password field so the visitor knows what they hit.
  description: string;
  children: React.ReactNode;
}
export function AdminPasswordGate({
  description,
  children
}: AdminPasswordGateProps) {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === ACCESS_PHRASE;
    } catch {
      return false;
    }
  });
  const [entry, setEntry] = useState('');
  const [error, setError] = useState('');
  if (unlocked) return <>{children}</>;
  const handleUnlock = (event: React.FormEvent) => {
    event.preventDefault();
    if (entry.trim() !== ACCESS_PHRASE) {
      setError('That password is incorrect.');
      return;
    }
    try {
      // Session-scoped, so closing the tab re-locks the page. Shared across
      // every gated directory, so unlocking one unlocks the others.
      sessionStorage.setItem(STORAGE_KEY, ACCESS_PHRASE);
    } catch {
      // Private browsing can block storage; unlocking still works for this view.
    }
    setError('');
    setUnlocked(true);
  };
  return <div className="bg-white" data-id="element-109" data-mp="2qssbgziir1d3">
      <div className="max-w-md mx-auto px-4 py-20 md:py-28" data-id="element-110" data-mp="2qrz0fmcj1pp5">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100" aria-hidden="true" data-id="element-111" data-mp="2ltxg6y3d7kc3">
          <LockIcon className="h-5 w-5 text-gray-500" data-id="element-112" data-mp="3jc7dcjmge9ke" />
        </span>
        <h1 className="mt-5 text-2xl font-bold text-gray-900" data-id="element-113" data-mp="3ei35ly5d5xi9">Internal page</h1>
        <p className="mt-2 text-gray-600 leading-relaxed" data-id="element-114" data-mp="1bjwlj1y1s8i">{description}</p>
        <form onSubmit={handleUnlock} className="mt-8" data-id="element-115" data-mp="6a4wfhdaa9wf">
          <label htmlFor="directory-password" className="block text-sm font-semibold text-gray-900" data-id="element-116" data-mp="68tw5rj1rka2">
            Password
          </label>
          <input id="directory-password" type="password" value={entry} autoFocus autoComplete="current-password" onChange={e => {
          setEntry(e.target.value);
          if (error) setError('');
        }} aria-invalid={Boolean(error)} aria-describedby={error ? 'directory-password-error' : undefined} className={`mt-2 w-full rounded-lg border px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-transparent ${error ? 'border-red-400' : 'border-gray-300'}`} data-id="element-117" data-mp="3snvaovxt1xtq" />
          {error && <p id="directory-password-error" role="alert" className="mt-2 text-sm text-red-600" data-id="element-118" data-mp="2si1bi6oh1v38">
              {error}
            </p>}
          <button type="submit" className="mt-5 w-full rounded-lg bg-[#e8594f] px-6 py-3 font-bold text-white transition-colors hover:bg-[#d54a41] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8594f] focus-visible:ring-offset-2" data-id="element-119" data-mp="327ulx4cvo45w">
            Unlock
          </button>
        </form>
      </div>
    </div>;
}